/**
 * Core compute function for building indicators
 * Enables functional, immutable transformations on arrays of data
 * 
 * @param data Array of items to transform
 * @param propertyName Name of the property to add to each item
 * @param transform Function that calculates the value for each item
 * @param params Optional parameters to pass to the transform function
 * @returns New array with the computed property added to each item
 */
export function compute<T, K extends string, P, V>(
    data: T[],
    propertyName: K,
    transform: (item: T, index: number, allItems: T[], params: P) => V,
    params?: P
): Array<T & { [P in K]: V }> {
    const result: Array<T & { [P in K]: V }> = [];
    
    for (let index = 0; index < data.length; index++) {
        const item = data[index];
        
        // Create a combined array that includes both original items and processed results
        // This allows the transform function to access previously computed values
        const combinedItems = data.map((originalItem, i) => {
            if (i < index) {
                // For already processed items, use the result (which includes computed properties)
                return result[i];
            }
            // For current and future items, use the original
            return originalItem;
        }) as T[];
        
        const transformedItem = {
            ...item,
            [propertyName]: transform(item, index, combinedItems, params as P)
        } as T & { [P in K]: V };
        
        result.push(transformedItem);
    }
    
    return result;
}

/**
 * Helper to chain multiple compute operations
 * Each operation receives the result of the previous one
 */
export function chain<T>(...operations: Array<(data: any) => any>): (data: T[]) => any {
    return (data: T[]) => {
        return operations.reduce((acc, operation) => operation(acc), data);
    };
}